/**
 * A single row in the shipments table. Shows details for a single shipment.
 */

import React, { useState } from "react";
import Column from "@amzn/meridian/column";
import { TableRow, TableCell } from "@amzn/meridian/table";
import PropTypes from "prop-types";

import Button from "@amzn/meridian/button";
import Icon from "@amzn/meridian/icon";
import Link from "@amzn/meridian/link";
import Text from "@amzn/meridian/text";
import Tooltip from "@amzn/meridian/tooltip";
import chevronDownSmallTokens from "@amzn/meridian-tokens/base/icon/chevron-down-small";
import chevronRightSmallTokens from "@amzn/meridian-tokens/base/icon/chevron-right-small";
import copyTokens from "@amzn/meridian-tokens/base/icon/copy";

import RiverModal from "../river/river-modal";
import IssuesModal from "../river/issues-modal";
import incomingShipmentPropType from "../../prop-types/incoming-shipment";
import useIncomingShipmentsViewPreferences from "../../hooks/use-incoming-shipments-view-preferences";
import useWarehouseData from "../../hooks/use-warehouse-data";
import { convertUnix } from "../../utilities/convert-unix";
import { getWarehouseAttributes } from "../../utilities/get-warehouse-attributes";
import {
  getVendorLink,
  getTransshipmentLink,
  getProcurementURL,
} from "../../utilities/links";
import Row from "@amzn/meridian/row";
import Alert from "@amzn/meridian/alert";
import Toaster from "@amzn/meridian/toaster";
import ExceptionTag from "./exception-tag";
import {
  loadIdDisplayLength,
  unscheduledVendorName,
  notYetManifestedName,
  filterDisplayAppointments,
} from "../Constants";
import EventIcon from "../event/event-icon";
import { getSummaryQuantity } from "../../utilities/parent-child-conversion";

const IncomingShipmentsTableRow = (props) => {
  const { incomingShipment, warehouseId } = props;

  const warehouseData = useWarehouseData();
  const { business_type, region_id, time_zone, wms } =
    getWarehouseAttributes({
      warehouseId: warehouseId,
      warehouseParams: warehouseData,
    });

  const [viewPreferences] = useIncomingShipmentsViewPreferences();

  const visibleColumns = viewPreferences.get("columns");
  const [expandRow, setExpandrow] = useState(false);

  const [toasts, setToasts] = useState([]);
  const onCloseToast = (id) => setToasts(toasts.filter((t) => t.id !== id));
  const onOpenToast = (loadId) =>
    setToasts(toasts.concat({ id: loadId, timeout: 1500 }));

  // Resolve the appointment id backing a given scheduled arrival time so the
  // Unified Portal deep-link points at the correct appointment. scheduledArrivals
  // is filtered (CLOSED appointments dropped) and sorted, so a positional index
  // into the raw associatedAppointments key list no longer lines up.
  const getAppointmentIdForArrival = (scheduledArrival) => {
    if (!incomingShipment.associatedAppointments) {
      return undefined;
    }
    const matched = filterDisplayAppointments(
      Object.values(JSON.parse(incomingShipment.associatedAppointments))
    ).find((appointment) => appointment.scheduledArrivalTime === scheduledArrival);
    return matched ? matched.appointmentId : undefined;
  };

  // Map to correct worflow based on site parameters
  // TODO: See if we can plug in parameters
  const workflowMap = {
    1: {
      Mendel: [
        {
          title: "Failed Temp Test",
          url: "https://river.amazon.com/SEA53/workflows?buildingType=amznmndl&q0=d4775f0b-b726-407d-94db-64e703c3a8cb&q1=d7ed5d53-1189-4238-9231-19e455217d2d&id=d7ed5d53-1189-4238-9231-19e455217d2d",
        },
        {
          title: "Pallet Too Tall",
          url: "https://river.amazon.com/SEA53/workflows?buildingType=amznmndl&q0=d4775f0b-b726-407d-94db-64e703c3a8cb&q1=a10a0c11-67fe-4f49-9b33-62be79d09b0d&id=a10a0c11-67fe-4f49-9b33-62be79d09b0d",
        },
        {
          title: "Wrong Stores Pallet",
          url: "https://river.amazon.com/SEA53/workflows?buildingType=amznmndl&q0=d4775f0b-b726-407d-94db-64e703c3a8cb&q1=c47eb9ef-af9d-442a-8d4a-dd08914b92f5&id=c47eb9ef-af9d-442a-8d4a-dd08914b92f5",
        },
        {
          title: "Early Delivery Arrivals",
          url: "https://river.amazon.com/SEA53/workflows?buildingType=amznmndl&q0=d4775f0b-b726-407d-94db-64e703c3a8cb&q1=ee7d411e-545e-429b-8c27-d3864d0fc0f1&id=ee7d411e-545e-429b-8c27-d3864d0fc0f1",
        },
        {
          title: "Other",
          url: "https://river.amazon.com/?org=amznmndl&buildingId=SEA53&q0=d4775f0b-b726-407d-94db-64e703c3a8cb",
        },
      ],
      Malone: [
        {
          title: "Failed Temp Test",
          url: "https://river.amazon.com/SEA53/workflows?buildingType=amznmndl&q0=d4775f0b-b726-407d-94db-64e703c3a8cb&q1=d7ed5d53-1189-4238-9231-19e455217d2d&id=d7ed5d53-1189-4238-9231-19e455217d2d",
        },
        {
          title: "Pallet Too Tall",
          url: "https://river.amazon.com/SEA53/workflows?buildingType=amznmndl&q0=d4775f0b-b726-407d-94db-64e703c3a8cb&q1=a10a0c11-67fe-4f49-9b33-62be79d09b0d&id=a10a0c11-67fe-4f49-9b33-62be79d09b0d",
        },
        {
          title: "Wrong Stores Pallet",
          url: "https://river.amazon.com/SEA53/workflows?buildingType=amznmndl&q0=d4775f0b-b726-407d-94db-64e703c3a8cb&q1=c47eb9ef-af9d-442a-8d4a-dd08914b92f5&id=c47eb9ef-af9d-442a-8d4a-dd08914b92f5",
        },
        {
          title: "Early Delivery Arrivals",
          url: "https://river.amazon.com/SEA53/workflows?buildingType=amznmndl&q0=d4775f0b-b726-407d-94db-64e703c3a8cb&q1=ee7d411e-545e-429b-8c27-d3864d0fc0f1&id=ee7d411e-545e-429b-8c27-d3864d0fc0f1",
        },
        {
          title: "Other",
          url: "https://river.amazon.com/?org=amznmndl&buildingId=SEA53&q0=d4775f0b-b726-407d-94db-64e703c3a8cb",
        },
      ],
      UFF: [
        {
          title: "PO Rejections",
          url: "https://river.amazon.com/BOS21/workflows?buildingType=UFFSCExecution&q0=2fad020b-108a-493c-b733-85b8d938956f&id=2fad020b-108a-493c-b733-85b8d938956f",
        },
        // {
        //   "title": "Full Pallet Short",
        //   "url": "
        // },
        {
          title: "PO not in AFTLite/Receive",
          url: "https://river.amazon.com/BOS21/workflows?buildingType=UFFSCExecution&q0=d9064ab3-7b54-4dc3-8f8d-3a530f438b39&id=d9064ab3-7b54-4dc3-8f8d-3a530f438b39",
        },
        {
          title: "Vendor/Whole Foods Compliance",
          url: "https://river.amazon.com/BOS21/workflows?buildingType=UFFSCExecution&q0=d167d18f-8a95-4bfc-9b87-83f15a87e7a5&id=d167d18f-8a95-4bfc-9b87-83f15a87e7a5",
        },
        {
          title: "Other",
          url: "https://river.amazon.com/BOS21/home?buildingType=UFFSCExecution",
        },
      ],
      SSD: [
        {
          "title": "Vendor Compliance",
          "url": "https://river.amazon.com/?org=SSD_SC&q0=90c05273-bc82-4cbb-bc61-f3210ce2c03f&q1=72568353-ad8b-400b-93bf-7abd588d3f4e"
        },
        {
          "title": "DC Compliance",
          "url": "https://river.amazon.com/?org=SSD_SC&q0=90c05273-bc82-4cbb-bc61-f3210ce2c03f&q1=0719a240-b3d7-473f-9970-dede655d16a2"
        },
        {
          "title": "Rejections",
          "url": "https://river.amazon.com/GYR2/workflows?buildingType=SSD_SC&q0=90c05273-bc82-4cbb-bc61-f3210ce2c03f&q1=84113b48-3af2-4a9b-819b-e070978ff5b1&id=84113b48-3af2-4a9b-819b-e070978ff5b1"
        }
      ],
      Go: [
        {
          "title": "Amazon Go Workflows",
          "url": "https://river.amazon.com/?org=amazon_go&q0=85914a4a-882a-498e-afb8-20c88c839da9"
        }
      ]
    },
    2: {
      UFF: [
        {
          title: "Other",
          url: "https://river.amazon.com/LHR16/home?buildingType=aft_lite_fc&q0=ce9abbea-c39e-4860-bc14-0154f653554b&q1=4962b4d4-23f5-4bdc-afd3-0bbff7f750a8",
        },
      ],
    },
    3: {
      UFF: [
        {
          title: "Other",
          url: "https://river.amazon.com/LHR16/home?buildingType=aft_lite_fc&q0=ce9abbea-c39e-4860-bc14-0154f653554b&q1=4962b4d4-23f5-4bdc-afd3-0bbff7f750a8",
        },
      ],
      SSD: [
        {
          "title": "Barcode Not Linked",
          "url": "https://t.corp.amazon.com/create/templates/add31cb9-d6c6-4dca-90d6-4a4851db6fa3"
        },
        {
          "title": "Barcode Links Wrong Item",
          "url": "https://t.corp.amazon.com/create/templates/06fb65a6-4eab-4acf-a2f5-d19616e7e445"
        },
        {
          "title": "Barcode Links Multiple ASINs",
          "url": "https://t.corp.amazon.com/create/templates/dea050fc-5bb2-4bf9-8d66-2f2d91d15ca7"
        },
        {
          "title": "Expiration Date Issue",
          "url": "https://t.corp.amazon.com/create/templates/83bf86a3-04f9-4f02-be99-829b042d2406"
        },
        {
          "title": "Shelf Life Pad Time",
          "url": "https://t.corp.amazon.com/create/templates/25569a41-eff6-43d9-abcb-9eac307af769"
        },
        {
          "title": "Wrong Temperature Type",
          "url": "https://t.corp.amazon.com/create/templates/cfafee03-72a6-457f-843d-05a0fedecb51"
        },
        {
          "title": "Image Update",
          "url": "https://t.corp.amazon.com/create/templates/29fbb202-2499-4393-865c-5dc1dbe2cddb"
        },
        {
          "title": "Dimensions/Weight Update",
          "url": "https://t.corp.amazon.com/create/templates/a3f013bc-9aa6-4b9e-aa7f-14dc633388bf"
        },
        {
          "title": "Title Update",
          "url": "https://t.corp.amazon.com/create/templates/21eca97c-4e01-4fff-a116-a0a6d412a240"
        },
        {
          "title": "High Unit Overage/Misship",
          "url": "https://t.corp.amazon.com/create/templates/0c894242-9f53-429f-8c60-42dddcbbf4f5"
        },
        {
          "title": "No PO Found",
          "url": "https://t.corp.amazon.com/create/templates/c1f04236-6584-4f17-91b4-0a667601f970"
        },
        {
          "title": "Vendor Compliance",
          "url": "https://t.corp.amazon.com/create/templates/ecd4f109-6315-4893-a496-8b2d78137490"
        },
        {
          "title": "Vendor Damage",
          "url": "https://t.corp.amazon.com/create/templates/bb74bd1c-05be-4a8e-a9b4-07ee18812fa1"
        }
      ]
    },
  };

  const riverWorkflows = workflowMap[region_id][business_type]
    ? workflowMap[region_id][business_type]
    : [];

  // Helper function to detect JP Swift sites (region_id = 3 AND business_type = 'SSD')
  const isJPSwiftSite = () => {
    return region_id === 3 && business_type === 'SSD';
  };

  return (
    <TableRow key={incomingShipment.id} rowComponents={props.rowComponents}>
      <Toaster toasts={toasts} onCloseToast={onCloseToast}>
        {(toast) => (
          <Alert toast={true} onClose={toast.onClose}>
            {`${toast.id} copied to clipboard`}
          </Alert>
        )}
      </Toaster>
      {visibleColumns.loadType && (
        <TableCell alignmentHorizontal="center">
          <Text tag="p">
            {incomingShipment.isvmi === 'Y' && incomingShipment.hasAsn === 'Y' ? 'VMI Vendor' : incomingShipment.loadType}
          </Text>
        </TableCell>
      )}
      {visibleColumns.source && (
        <TableCell alignmentHorizontal="center">
          {incomingShipment.isvmi === 'Y' && incomingShipment.hasAsn === 'Y' &&
            incomingShipment.distributorName ? (
            <Tooltip position="end" title={`VMI - ${incomingShipment.distributorName}`}>
              <Text tag="p">{incomingShipment.source}</Text>
            </Tooltip>
          ) : incomingShipment.loadType === "Vendor" &&
            incomingShipment.distributorName ? (
            <Tooltip position="end" title={incomingShipment.distributorName}>
              <Text tag="p">{incomingShipment.source}</Text>
            </Tooltip>
          ) : (
            <Text tag="p">{incomingShipment.source}</Text>
          )}
        </TableCell>
      )}
      {visibleColumns.loadId && (
        <TableCell alignmentHorizontal="center">
          <Column>
            {typeof incomingShipment.loadId === "string" ? (
              [unscheduledVendorName, notYetManifestedName].includes(
                incomingShipment.loadId
              ) ? (
                <Text truncate={!expandRow}>{incomingShipment.loadId}</Text>
              ) : (
                <Row alignmentHorizontal="center">
                  <Tooltip
                    position="end"
                    title={incomingShipment.loadId}
                    id={`${incomingShipment.id}-${incomingShipment.loadId}-toolTip`}
                  >
                    <Link
                      href={
                        incomingShipment.loadType === "Vendor"
                          ? getVendorLink(
                            region_id,
                            wms,
                            incomingShipment.loadId
                          )
                          : getTransshipmentLink(
                            region_id,
                            wms,
                            incomingShipment.loadId
                          )
                      }
                      target={"_blank"}
                      rel={"noopener noreferrer"}
                    >
                      <Text truncate={!expandRow}>
                        {incomingShipment.loadId.length > loadIdDisplayLength
                          ? `${incomingShipment.loadId.substring(
                            0,
                            loadIdDisplayLength
                          )}...`
                          : incomingShipment.loadId}
                      </Text>
                    </Link>
                  </Tooltip>
                  <Button
                    type="icon"
                    size="small"
                    onClick={() => {
                      navigator.clipboard.writeText(incomingShipment.loadId);
                      onOpenToast(incomingShipment.loadId);
                    }}
                  >
                    <Icon tokens={copyTokens} />
                  </Button>
                </Row>
              )
            ) : incomingShipment.loadId.length > 0 ? (
              incomingShipment.loadId.map((loadId) =>
                loadId === "Not Yet Manifested" ? (
                  <Text truncate={!expandRow}>{loadId}</Text>
                ) : (
                  <Row>
                    <Tooltip
                      position="end"
                      title={incomingShipment.loadId}
                      id={`${incomingShipment.id}-${incomingShipment.loadId}-toolTip`}
                    >
                      <Link
                        href={
                          incomingShipment.loadType === "Vendor"
                            ? getProcurementURL(region_id, loadId)
                            : getTransshipmentLink(region_id, wms, loadId)
                        }
                        target={"_blank"}
                        rel={"noopener noreferrer"}
                      >
                        <Text truncate={!expandRow}>
                          {loadId.length > loadIdDisplayLength
                            ? `${loadId.substring(0, loadIdDisplayLength)}...`
                            : loadId}
                        </Text>
                      </Link>
                    </Tooltip>
                    <Button
                      type="icon"
                      size="small"
                      onClick={() => {
                        navigator.clipboard.writeText(incomingShipment.loadId);
                        onOpenToast(incomingShipment.loadId);
                      }}
                    >
                      <Icon tokens={copyTokens} />
                    </Button>
                  </Row>
                )
              )
            ) : null}
            {incomingShipment.criticalAsinEvents && (
              <Row alignmentHorizontal="center">
                {incomingShipment.criticalAsinEvents.map((criticalEvent) => (
                  <EventIcon
                    event={criticalEvent.eventName}
                    regionId={region_id}
                  />
                ))}
              </Row>
            )}
            {incomingShipment.arrivalDatetime[0] ? (
              <>
                <ExceptionTag shipment={{ exceptionId: "arrived" }} />
                <ExceptionTag shipment={{ exceptionId: "dockCheckin" }} />
              </>
            ) : incomingShipment.isvmi === 'Y' && incomingShipment.hasAsn === 'Y' ? (
              <>
                <ExceptionTag shipment={{ exceptionId: "asnGenerated" }} />
              </>
            ) : null }
          </Column>
        </TableCell>
      )}
      {visibleColumns.tempZone && (
        <TableCell alignmentHorizontal="center">
          <Text tag="p">{incomingShipment.tempZone}</Text>
        </TableCell>
      )}
      {visibleColumns.criticalAsinEvents && (
        <TableCell alignmentHorizontal="center">
          <Column>
            {incomingShipment.criticalAsinEvents &&
              incomingShipment.criticalAsinEvents.map((criticalEvent) => (
                <Text tag="p">{criticalEvent.eventName}</Text>
              ))}
          </Column>
        </TableCell>
      )}
      {visibleColumns.status && (
        <TableCell alignmentHorizontal="center">
          <Text tag="p">{incomingShipment.status}</Text>
        </TableCell>
      )}
      {visibleColumns.scheduledArrivals && (
        <TableCell alignmentHorizontal="center">
          <Column>
            {incomingShipment.scheduledArrivals.map((scheduledArrival) =>
              scheduledArrival ? (
                <Tooltip
                  position="end"
                  title={`Source: ${incomingShipment.isvmi === 'Y' && incomingShipment.hasAsn === 'Y'
                    ? 'ASN Creation Day'
                    : incomingShipment.firstAppointmentSource || 'Unknown'
                    }`}
                  id={`${incomingShipment.id}-${incomingShipment.tempZone}-toolTip`}
                >
                  {incomingShipment.firstAppointmentSource !== "SCIP" &&
                    incomingShipment.associatedAppointments ? (
                    <Link
                      href={`https://unified-portal-na.corp.amazon.com/#/appointment?searchType=IAS&searchCategory=appointment&searchIds=${getAppointmentIdForArrival(
                        scheduledArrival
                      )}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Text tag="p">
                        {convertUnix(scheduledArrival, time_zone)}
                      </Text>
                    </Link>
                  ) : (
                    <Text tag="p">
                      {convertUnix(scheduledArrival, time_zone)}
                    </Text>
                  )}
                </Tooltip>
              ) : null
            )}
          </Column>
        </TableCell>
      )}
      {visibleColumns.expectedArrivalTime && (
        <TableCell alignmentHorizontal="center">
          <Column>
            {incomingShipment.expectedArrivalTime.map((arrival, index) =>
              incomingShipment.expectedArrivalTimeSource[index] ? (
                <Tooltip
                  position="end"
                  title={`Source: ${incomingShipment.expectedArrivalTimeSource[index]}`}
                  id={`${incomingShipment.id}-${incomingShipment.tempZone}-expected-arrival-toolTip`}
                >
                  <Text tag="p">
                    {arrival ? convertUnix(arrival, time_zone) : null}
                  </Text>
                </Tooltip>
              ) : (
                <Text tag="p">
                  {arrival ? convertUnix(arrival, time_zone) : null}
                </Text>
              )
            )}
          </Column>
        </TableCell>
      )}
      {visibleColumns.arrivalDatetime && (
        <TableCell alignmentHorizontal="center">
          <Column>
            {incomingShipment.arrivalDatetime.map((arrival, index) =>
              incomingShipment.arrivalDatetimeSource[index] ? (
                <Tooltip
                  position="end"
                  title={`Source: ${incomingShipment.arrivalDatetimeSource[index]}`}
                  id={`${incomingShipment.id}-${incomingShipment.tempZone}-arrival-toolTip`}
                >
                  <Text tag="p">
                    {arrival ? convertUnix(arrival, time_zone) : null}
                  </Text>
                </Tooltip>
              ) : (
                <Text tag="p">
                  {arrival ? convertUnix(arrival, time_zone) : null}
                </Text>
              )
            )}
          </Column>
        </TableCell>
      )}
      {visibleColumns.expectedUnits && (
        <TableCell alignmentHorizontal="center">
          <Tooltip
            position="end"
            title={
              <div style={{ whiteSpace: "pre-line" }}>
                {`Source: ${incomingShipment.expectedUnitSource}`
                  .split(", ")
                  .join("\n")}
              </div>
            }
            id={`${incomingShipment.id}-${incomingShipment.tempZone}-expectedUnitSource-toolTip`}
          >
            <Text tag="p">
              {getSummaryQuantity(
                incomingShipment,
                'expectedUnits',
                'childExpectedUnits'
              ).toLocaleString()}
            </Text>
          </Tooltip>
        </TableCell>
      )}
      {visibleColumns.expectedAsrsUnits && (
        <TableCell alignmentHorizontal="center">
          <Text tag="p">
            {getSummaryQuantity(
              incomingShipment,
              'expectedAsrsUnits',
              'childExpectedAsrsUnits'
            ).toLocaleString()}
          </Text>
        </TableCell>
      )}
      {visibleColumns.expectedAsrsUnitsPercent && (
        <TableCell alignmentHorizontal="center">
          <Text tag="p">
            {incomingShipment.expectedAsrsUnitsPercent
              ? `${incomingShipment.expectedAsrsUnitsPercent.toLocaleString()}%`
              : `0%`}
          </Text>
        </TableCell>
      )}
      {visibleColumns.expectedPallets && (
        <TableCell alignmentHorizontal="center">
          <Text tag="p">
            {incomingShipment.expectedPallets
              ? incomingShipment.expectedPallets.toLocaleString()
              : ""}
          </Text>
        </TableCell>
      )}
      {visibleColumns.shipmentId && (
        <TableCell alignmentHorizontal="center">
          <Column>
            {incomingShipment.shipmentId.map((shipmentId) => (
              <Text tag="p">{shipmentId}</Text>
            ))}
            {<Text tag="p">{incomingShipment.stopSequence}</Text>}
          </Column>
        </TableCell>
      )}
      {visibleColumns.ocrVendor && (
        <TableCell alignmentHorizontal="center">
          <Text tag="p">{incomingShipment.ocrVendor}</Text>
        </TableCell>
      )}
      {visibleColumns.scac && (
        <TableCell alignmentHorizontal="center">
          <Text tag="p">{incomingShipment.scac}</Text>
        </TableCell>
      )}
      <TableCell alignmentHorizontal="center">
        <Button
          type="icon"
          size="small"
          onClick={() => {
            expandRow ? setExpandrow(false) : setExpandrow(true);
          }}
        >
          {expandRow ? (
            <Icon tokens={chevronDownSmallTokens}>Collapse rows</Icon>
          ) : (
            <Icon tokens={chevronRightSmallTokens}>Expand rows</Icon>
          )}
        </Button>
        {expandRow && (
          <Column spacing="300">
            {isJPSwiftSite() ? (
              <IssuesModal
                buttonTitle={"Issues"}
                data={incomingShipment}
                modalTitle={"Issues"}
                riverWorkflows={riverWorkflows}
              />
            ) : (
              <RiverModal
                buttonTitle={"River"}
                data={incomingShipment}
                modalTitle={"River"}
                riverWorkflows={riverWorkflows}
              />
            )}
          </Column>
        )}
      </TableCell>
    </TableRow>
  );
};

IncomingShipmentsTableRow.propTypes = {
  incomingShipment: incomingShipmentPropType.isRequired,
  rowComponents: TableRow.propTypes.rowComponents,
  warehouseId: PropTypes.string.isRequired,
};

export default IncomingShipmentsTableRow;
